import * as React from 'react';
import { Button } from './button';
import { Input } from './input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './select';

export interface SearchableSelectOption {
  value: string;
  primaryLabel: string;
  secondaryLabel?: string;
}

interface SearchableSelectProps {
  id: string;
  options: readonly SearchableSelectOption[];
  status: 'loading' | 'ready' | 'error';
  value: string;
  onValueChange: (value: string) => void;
  onRetry: () => void;
  loadingLabel: string;
  emptyLabel: string;
  errorLabel: string;
  retryLabel: string;
  searchPlaceholder: string;
  /** Shown in the trigger while no value is chosen. */
  placeholder?: string;
  'aria-describedby'?: string;
  'aria-label'?: string;
}

/**
 * SL-0056 (unit 3a), DEC-05/DEC-09: a single-value searchable select over a catalog read, built
 * entirely on the existing `./select` wrappers — never importing `@radix-ui/react-select` directly
 * (Resolution 3), so free typing coexists with Radix's own keyboard handling without a new
 * dependency. i18n-agnostic (Resolution 2): every user-visible string arrives as a prop, mirroring
 * `DataTable`'s own `emptyMessage`-as-prop precedent, so this component never calls
 * `useTranslation` itself.
 *
 * DEC-09 divergence (Resolution 4, `RCPT-SL3A-DR004`): `filterSearchableSelectOptions` and the
 * option-rendering JSX below are colocated with this shell component in the same file, rather than
 * exported as their own hook independent of the shell, as DEC-09's letter requires.
 * `filterSearchableSelectOptions` itself remains an exported pure function with no dependency on
 * the shell, so a later multi-select unit can still import and reuse it from here.
 */
export function filterSearchableSelectOptions(
  options: readonly SearchableSelectOption[],
  query: string,
): SearchableSelectOption[] {
  const needle = query.trim().toLowerCase();
  if (needle === '') {
    return [...options];
  }
  return options.filter((option) =>
    `${option.primaryLabel}${option.secondaryLabel ?? ''}`.toLowerCase().includes(needle),
  );
}

// Resolution 3: the filter input stops every keydown from reaching Radix's own `SelectContent`
// keydown handler EXCEPT this set — `ArrowDown`/`ArrowUp` (item navigation), `Enter` (select the
// highlighted item), `Escape` (close — the DR-03 focus-containment case), and `Tab`. `Tab`'s own
// reasoning was corrected during SL-0056 execution (`RCPT-EXEC-SL0056-TAB`): Radix's own
// `SelectContent` handler DOES call `preventDefault()` on `Tab`, but focus containment itself does
// NOT depend on that call firing — `@radix-ui/react-select` composes `FocusScope` onto the same
// content node with `trapped: true`, and `@radix-ui/react-focus-scope`'s own document-level
// `focusin` listener snaps focus back into the popover whenever it lands outside, independent of
// any `preventDefault()`. What forwarding `Tab` actually buys is the ABSENCE of that round trip —
// without it, focus would leave and get yanked back, firing spurious focus events a screen reader
// announces (and potentially a native scroll). That is a real accessibility refinement, not the
// containment guarantee itself, and DEC-05's keyboard-operable claim still favours keeping it.
// `Home`/`End` are deliberately NOT in this set (Human-settled, `RCPT-CR-HOMEEND`): they keep their
// conventional text-cursor meaning inside this filter input rather than jumping to Radix's first/
// last item, even though Radix's own handler treats them identically to the arrow keys.
const KEYS_FORWARDED_TO_RADIX = new Set(['ArrowDown', 'ArrowUp', 'Enter', 'Escape', 'Tab']);

export function SearchableSelect({
  id,
  options,
  status,
  value,
  onValueChange,
  onRetry,
  loadingLabel,
  emptyLabel,
  errorLabel,
  retryLabel,
  searchPlaceholder,
  placeholder,
  'aria-describedby': ariaDescribedBy,
  'aria-label': ariaLabel,
}: SearchableSelectProps) {
  const [query, setQuery] = React.useState('');
  const filteredOptions = filterSearchableSelectOptions(options, query);

  return (
    <div className="flex items-center gap-2">
      <Select
        value={value}
        onValueChange={onValueChange}
        disabled={status !== 'ready'}
        // Diagnosed during SL-0056 Activity 2's own integration tests (a form's `reset(...)`
        // re-selecting an already-chosen option): the filter query is this component's own
        // local state, so it otherwise survives the popover closing and reopening on the SAME
        // mounted instance — a stale query then gets typed INTO again on the next open, filtering
        // against a query that no longer matches anything. Clearing it on close is the fix.
        onOpenChange={(open) => {
          if (!open) {
            setQuery('');
          }
        }}
      >
        <SelectTrigger id={id} aria-describedby={ariaDescribedBy} aria-label={ariaLabel}>
          {status === 'loading' && loadingLabel}
          {status === 'error' && errorLabel}
          {status === 'ready' && <SelectValue placeholder={placeholder} />}
        </SelectTrigger>
        <SelectContent>
          <Input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={(event) => {
              if (!KEYS_FORWARDED_TO_RADIX.has(event.key)) {
                event.stopPropagation();
              }
            }}
            placeholder={searchPlaceholder}
            className="mb-1"
          />
          {filteredOptions.length === 0 ? (
            <div className="px-2 py-1.5 text-sm text-muted-foreground">{emptyLabel}</div>
          ) : (
            filteredOptions.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                <span className="flex flex-col">
                  <span>{option.primaryLabel}</span>
                  {option.secondaryLabel !== undefined && (
                    <span className="text-xs text-muted-foreground">{option.secondaryLabel}</span>
                  )}
                </span>
              </SelectItem>
            ))
          )}
        </SelectContent>
      </Select>
      {status === 'error' && (
        <Button variant="ghost" size="sm" onClick={onRetry}>
          {retryLabel}
        </Button>
      )}
    </div>
  );
}
